import { Fn }		from "ts-base/fn";
import { Arrays }	from "ts-base/arrays";
import { Monoid }	from "ts-base/monoid";

//-------------------------------------------------------------------------
//## private implementation

// has to be defined before the namespace AHtml so AHtml.empty can use it during initialization
class AHtmlImpl<A> {
	constructor(readonly parts:ReadonlyArray<Part<A>>) {}
}

type Part<A>	= Raw | Escape | Attach<A>;
class Raw		{ constructor(readonly content:string)	{} }
class Escape	{ constructor(readonly content:string)	{} }
class Attach<A>	{ constructor(readonly content:A)		{} }

const escapeEntities	= (input:string):string	=>
	input
	.replace(/&/g, "&amp;")
	.replace(/</g, "&lt;")
	.replace(/>/g, "&gt;")
	.replace(/"/g, "&quot;");
	// .replace(/'/g, "&apos;")

//------------------------------------------------------------------------------
//## data

export type AHtml<A>	= AHtmlImpl<A>;

export namespace AHtml {
	//-----------------------------------------------------------------------------
	//## checks

	export const is	= (it:unknown):it is AHtml<unknown>	=> it instanceof AHtmlImpl;

	//-------------------------------------------------------------------------
	//## constructors

	/** tagged template function */
	export const literal	= <A>(literals:TemplateStringsArray, ...placeholders:ReadonlyArray<Fragment<A>>):AHtml<A> => {
		const out:Array<Part<A>>	= [];
		out.push(new Raw(literals[0]));
		for (let i=0; i<placeholders.length; i++) {
			const placeholder	= placeholders[i];
			if (is(placeholder)) {
				for (const part of placeholder.parts) {
					out.push(part);
				}
			}
			else {
				out.push(new Escape(placeholder));
			}
			out.push(new Raw(literals[i+1]));
		}
		return of(out);
	};

	export const unsafe	= (raw:string):AHtml<never>	=>
		of<never>([ new Raw(raw) ]);

	export const escaped	= (raw:string):AHtml<never>	=>
		of<never>([ new Escape(raw) ]);

	export const attach	= <A>(raw:A):AHtml<A>	=>
		of([ new Attach<A>(raw) ]);

	const of	= <T>(parts:ReadonlyArray<Part<T>>):AHtml<T>	=>
		new AHtmlImpl(parts);

	//------------------------------------------------------------------------------
	//## monoid

	export const empty:AHtml<never>	= of([]);

	export const concat	= <A>(a:AHtml<A>, b:AHtml<A>):AHtml<A>	=>
		of(a.parts.concat(b.parts));

	export const monoid	= <A>():Monoid<AHtml<A>>	=> ({
		empty:	empty,
		concat:	concat,
	});

	//------------------------------------------------------------------------------
	//## monoid functions

	export const joinN	= <A>(...items:ReadonlyArray<AHtml<A>>):AHtml<A>	=>
		join(items);

	export const join	= <A>(items:ReadonlyArray<AHtml<A>>):AHtml<A>	=>
		of(items.flatMap(it => it.parts));
		// items.reduce(concat, empty);

	export const joinWith	= <A>(items:ReadonlyArray<AHtml<A>>, separator:AHtml<A>):AHtml<A>	=>
		join(Arrays.intercalate(items)(separator));

	export const mapJoin	= <A, T>(items:ReadonlyArray<T>, func:(it:T)=>AHtml<A>):AHtml<A>	=>
		join(items.map(func));

	export const mapJoinWith	= <A, T>(items:ReadonlyArray<T>, func:(it:T)=>AHtml<A>, separator:AHtml<A>):AHtml<A>	=>
		joinWith(items.map(func), separator);

	//-----------------------------------------------------------------------------
	//## fragment

	/** to be put into placeholders */
	export type Fragment<A>	= AHtml<A> | string;

	//------------------------------------------------------------------------------
	//## rendering

	/** simplify drops attachments for compatibility with older code */
	export const renderString	= <A>(self:AHtml<A>):string	=>
		render(self, Fn.constant("")).content;

	export const render	= <A>(self:AHtml<A>, reference:Fn<A, string>):Rendered<A>	=> {
		const strings		= [];
		const attachments	= [];
		for (const part of self.parts) {
			if (part instanceof Raw) {
				strings.push(part.content);
			}
			else if (part instanceof Escape) {
				strings.push(escapeEntities(part.content));
			}
			else if (part instanceof Attach) {
				strings.push(escapeEntities(reference(part.content)));
				attachments.push(part.content);
			}
			else Fn.impossible(part);
		}
		return {
			content:		strings.join(""),
			attachments:	attachments,
		};
	};

	export type Rendered<A>	= {
		content:		string,
		attachments:	ReadonlyArray<A>,
	};
}

export const ahtml	= AHtml.literal;
